import { TextField, theme } from "@avenue-8/sidekick-ui";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { validateField } from "./editable-input-helpers";
import styled from "@emotion/styled";

type Variant =
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "subtitle1"
  | "subtitle2"
  | "body1"
  | "body2"
  | "caption"
  | "button"
  | "overline";

type Props = {
  name: string;
  value: string | null | undefined;
  type: "text" | "phone" | "email";
  placeholder?: string;
  onSave: (value: string) => void;
  onCancel: () => void;
  autoFocus?: boolean;
  onError?: (error: string | null) => void;
  styles?: {
    maxWidth?: string;
  };
  variant?: Variant;
};

const TinyTextField = styled(TextField)`
  & .MuiInputBase-root {
    height: 100% !important;
    padding: 0 0.25rem !important;
    & input {
      padding: 0 0.25rem !important;
    }
  }

  & .Mui-focused {
    padding: 0 0.25rem !important;
  }
`;

export const EditableInput = (props: Props) => {
  const {
    name,
    type,
    value,
    onSave,
    onCancel,
    onError,
    placeholder,
    autoFocus,
    variant = "body1",
  } = props;
  const [inputValue, setInputValue] = useState(value ?? "");
  const [error, setError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setInputValue(value ?? "");
    setError(null);
  }, [value]);

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    onError?.(error);
  }, [error, onError]);

  const inputTextStyle = useMemo(() => {
    const style = theme.typography[variant];
    return {
      fontSize: style.fontSize,
      fontWeight: style.fontWeight,
      letterSpacing: style.letterSpacing,
      lineHeight: "100%",
    };
  }, [variant]);

  const validateAndSave = useCallback(
    async (newValue: any) => {
      setIsValidating(true);

      try {
        const validationError = await validateField(type, newValue);

        if (validationError) {
          setError(validationError);
          setIsValidating(false);
          return;
        }

        setError(null);
        onSave(newValue);
      } catch {
        setError("Invalid value");
      } finally {
        setIsValidating(false);
      }
    },
    [type, onSave],
  );

  const handleCancel = useCallback(() => {
    setError(null);
    onCancel();
  }, [onCancel]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter" && !error && !isValidating) {
        event.preventDefault();
        validateAndSave(inputValue);
      } else if (event.key === "Escape") {
        event.preventDefault();
        handleCancel();
      }
    },
    [inputValue, error, isValidating, validateAndSave, handleCancel],
  );

  const handleBlur = useCallback(() => {
    if (error || isValidating) {
      handleCancel();
      return;
    }

    if (inputValue === value) {
      handleCancel();
      return;
    }

    validateAndSave(inputValue);
  }, [inputValue, value, error, isValidating, validateAndSave, handleCancel]);

  switch (type) {
    case "phone":
      return (
        <TinyTextField
          name={name}
          value={inputValue}
          onChange={(event) => {
            const value = event.target.value;
            let filtered = value.replace(/[^0-9+]/g, "");
            if (!value.startsWith("+")) {
              filtered = filtered.replace(/\+/g, "");
            } else {
              filtered = "+" + filtered.replace(/\+/g, "");
            }
            setInputValue(filtered);
            setError(null);
          }}
          placeholder={placeholder}
          onKeyDown={handleKeyDown}
          error={!!error}
          helperText={error || ""}
          size="small"
          variant="outlined"
          inputRef={inputRef}
          hideHelperTextSpace
          inputProps={{ onBlur: handleBlur, style: inputTextStyle }}
        />
      );

    case "email":
      return (
        <TinyTextField
          name={name}
          value={inputValue}
          onChange={(event) => {
            setInputValue(event.target.value);
            setError(null);
          }}
          placeholder={placeholder}
          onKeyDown={handleKeyDown}
          error={!!error}
          helperText={error || ""}
          size="small"
          variant="outlined"
          inputRef={inputRef}
          hideHelperTextSpace
          inputProps={{ onBlur: handleBlur, style: inputTextStyle }}
        />
      );

    case "text":
    default:
      return (
        <TinyTextField
          name={name}
          value={inputValue}
          onChange={(event) => {
            setInputValue(event.target.value);
            setError(null);
          }}
          placeholder={placeholder}
          onKeyDown={handleKeyDown}
          error={!!error}
          helperText={error || ""}
          size="small"
          variant="outlined"
          inputRef={inputRef}
          hideHelperTextSpace
          inputProps={{ onBlur: handleBlur, style: inputTextStyle }}
        />
      );
  }
};
